import React from 'react';
import { Box, Chip, Divider, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type {
  OperationProgressMetric,
  OperationProgressModel,
  OperationProgressStatus,
  OperationProgressStepStatus,
} from './operationProgress.types';
import { clampProgressPercent } from './operationProgressUtils';
import {
  formatOperationProgressDuration,
  getOperationProgressStatusChipColor,
  getOperationProgressStatusLabel,
} from './operationProgressPresentation';
import { OperationProgressActivityList } from './OperationProgressActivityList';
import { OperationProgressStepList } from './OperationProgressStepList';
import { useOperationProgressTiming } from './useOperationProgressTiming';
import { diagLog } from '../../api/grpc/webLogger';

interface OperationProgressBodyProps {
  model: OperationProgressModel;
  compact?: boolean;
  framed?: boolean;
  showTitle?: boolean;
  sx?: SxProps<Theme>;
  testId?: string;
}

export interface OperationProgressWindowProps {
  open: boolean;
  model: OperationProgressModel;
  onClose?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
}

export interface OperationProgressSurfaceProps {
  model: OperationProgressModel;
  compact?: boolean;
  sx?: SxProps<Theme>;
  testId?: string;
}

const metricColor = (theme: Theme, metric: OperationProgressMetric): string => {
  switch (metric.tone) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.primary.main;
  }
};

const progressColor = (theme: Theme, status: OperationProgressStatus): string => {
  switch (status) {
    case 'complete':
      return theme.palette.success.main;
    case 'cached':
      return theme.palette.info.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'waiting':
      return theme.palette.warning.main;
    default:
      return theme.palette.primary.main;
  }
};

const isResolvedOperationProgressStep = (status: OperationProgressStepStatus): boolean =>
  status === 'complete' || status === 'skipped';

export const OperationProgressBody: React.FC<OperationProgressBodyProps> = ({
  model,
  compact = false,
  framed = true,
  showTitle = true,
  sx,
  testId = 'operation-progress',
}) => {
  diagLog(
    'progress-watchdog',
    `render:body:start status=${model.status} percent=${model.percent ?? 'null'} processed=${model.processed ?? 'n/a'}/${model.total ?? 'n/a'} items=${(model.items ?? []).length}`
  );
  const theme = useTheme();
  const percent = clampProgressPercent(model.percent);
  const timing = useOperationProgressTiming(model);
  const { busy, terminal, waiting } = timing;
  const displayStatus: OperationProgressStatus = waiting ? 'waiting' : model.status;

  // Independent proof of event-loop life while the window is open (diagnostics
  // for the freeze investigation). The interval must NOT depend on the polled
  // model values: deps that change every poll would reset it forever.
  const modelRef = React.useRef(model);
  React.useEffect(() => {
    modelRef.current = model;
  }, [model]);

  React.useEffect(() => {
    if (!busy) {
      return undefined;
    }

    const heartbeatId = window.setInterval(() => {
      const current = modelRef.current;
      const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      const heapMb = memory ? Math.round(memory.usedJSHeapSize / 1_048_576) : 'n/a';
      diagLog(
        'progress-watchdog',
        `heartbeat operationId=${current.operationId ?? 'n/a'} status=${current.status} percent=${current.percent ?? 'null'} processed=${current.processed ?? 'n/a'}/${current.total ?? 'n/a'} hasFocus=${document.hasFocus()} heapMb=${heapMb}`
      );
    }, 500);
    return () => window.clearInterval(heartbeatId);
  }, [busy]);

  const accentColor = progressColor(theme, displayStatus);
  const metrics = model.metrics ?? [];
  const items = model.items ?? [];
  // The backend ledger grows by family x parameters (thousands of rows over a
  // run); re-rendering the full MUI list on every poll wedges the WebView2
  // renderer. The tail is all that matters live.
  const activityItems = items.length > 50 ? items.slice(-50) : items;
  const steps = model.steps ?? [];
  // A waiting status only forces indeterminate display when no determinate
  // progress was reported; user-input-wait phases keep their percent and ETA.
  const displayedPercent = percent === 100 && !terminal ? 99 : percent ?? (waiting
    ? null
    : terminal
      ? 100
      : null);
  const computedEtaMs = model.etaMs ?? null;
  const elapsedLabel = formatOperationProgressDuration(timing.elapsedMs);
  const waitElapsedLabel = formatOperationProgressDuration(timing.waitElapsedMs);
  const activeElapsedLabel = formatOperationProgressDuration(timing.activeElapsedMs);
  const lastUpdateAgeLabel = formatOperationProgressDuration(timing.lastUpdateAgeMs);
  const etaDurationLabel = formatOperationProgressDuration(computedEtaMs);
  const etaLabel = model.etaLabel ?? (etaDurationLabel ? `About ${etaDurationLabel} remaining` : undefined);
  const processedLabel = model.processed !== null && model.processed !== undefined && model.total
    ? `${model.processed} / ${model.total}`
    : model.processed !== null && model.processed !== undefined
      ? String(model.processed)
      : null;
  const longStepPlan = steps.length > 4;
  const stepSummaryLabel = steps.length > 0
    ? `${steps.filter((step) => isResolvedOperationProgressStep(step.status)).length} / ${steps.length}`
    : null;
  const etaMetricValue = metrics.find((metric) => metric.label === 'ETA')?.value;
  const summaryEtaLabel = etaLabel ?? etaMetricValue ?? (longStepPlan ? 'Pending' : undefined);
  const summaryCurrentLabel = model.currentItem ?? (longStepPlan ? 'Pending' : undefined);
  const summaryProcessedLabel = processedLabel ?? (longStepPlan ? 'Pending' : undefined);
  const summaryMetricLabels = new Set(['Step', 'ETA']);
  const summaryFacts = [
    stepSummaryLabel ? { label: 'Steps', value: stepSummaryLabel } : null,
    ...metrics
      .filter((metric) => metric.label !== 'Step' && metric.label !== 'ETA')
      .slice(0, 4)
      .map((metric) => ({ label: metric.label, value: metric.value })),
    summaryEtaLabel ? { label: 'ETA', value: summaryEtaLabel } : null,
    summaryCurrentLabel ? { label: 'Current', value: summaryCurrentLabel } : null,
    model.waitReason ? { label: 'Wait', value: model.waitReason } : null,
    summaryProcessedLabel ? { label: 'Processed', value: summaryProcessedLabel } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
  summaryFacts.forEach((fact) => summaryMetricLabels.add(fact.label));
  const operationFacts = [
    model.waitReason ? { label: 'Wait', value: model.waitReason } : null,
    waitElapsedLabel ? { label: 'Waiting', value: waitElapsedLabel } : null,
    activeElapsedLabel ? { label: 'Active', value: activeElapsedLabel } : null,
    model.currentItem ? { label: 'Current', value: model.currentItem } : null,
    processedLabel ? { label: 'Processed', value: processedLabel } : null,
    elapsedLabel ? { label: 'Elapsed', value: elapsedLabel } : null,
    busy && lastUpdateAgeLabel ? { label: 'Last update', value: `${lastUpdateAgeLabel} ago` } : null,
    model.cacheStatus ? { label: 'Cache', value: model.cacheStatus } : null,
  ].filter(Boolean)
    .filter((fact): fact is { label: string; value: string } =>
      Boolean(fact && !summaryMetricLabels.has(fact.label))
    );
  const supportingMetrics = metrics.filter((metric) => !summaryMetricLabels.has(metric.label));
  const progressLabel = displayedPercent === null ? 'Progress pending' : `${displayedPercent}%`;
  const progressStatusLabel = getOperationProgressStatusLabel(displayStatus);
  const progressValueProps = displayedPercent === null
    ? { 'aria-valuetext': `${progressLabel}. ${progressStatusLabel}` }
    : {
        'aria-valuenow': displayedPercent,
        'aria-valuetext': `${displayedPercent}% ${progressStatusLabel}${etaLabel ? `. ${etaLabel}` : ''}`,
      };

  diagLog('progress-watchdog', 'render:body:end');
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy={busy}
      data-testid={testId}
      sx={{
        width: '100%',
        minWidth: 0,
        ...sx,
      }}
    >
      <Stack spacing={compact ? layoutGap.row : 1.25}>
        {showTitle ? (
          <Stack
            direction="row"
            spacing={layoutGap.row}
            justifyContent="space-between"
            alignItems="flex-start"
            useFlexGap
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                {model.title}
              </Typography>
              {model.stepLabel ? (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {model.stepLabel}
                </Typography>
              ) : null}
            </Box>
            <Chip
              size="small"
              label={getOperationProgressStatusLabel(displayStatus)}
              color={getOperationProgressStatusChipColor(displayStatus)}
              variant="outlined"
              sx={{ flexShrink: 0, height: 24, fontWeight: 700 }}
            />
          </Stack>
        ) : null}

        <Box
          sx={{
            minWidth: 0,
            ...(framed
              ? {
                  p: compact ? 1.25 : 1.5,
                  borderRadius: `${borderRadius.md}px`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.82)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.9),
                }
              : {}),
          }}
        >
          <Stack spacing={compact ? layoutGap.row : 1.15}>
            {summaryFacts.length > 0 ? (
              <Stack
                direction="row"
                spacing={layoutGap.comfort}
                flexWrap="wrap"
                useFlexGap
                data-testid="operation-progress-summary"
              >
                {summaryFacts.map((fact) => (
                  <Chip
                    key={`${fact.label}:${fact.value}`}
                    size="small"
                    variant="outlined"
                    label={`${fact.label}: ${fact.value}`}
                    sx={{
                      maxWidth: '100%',
                      height: 24,
                      borderColor: alpha(theme.palette.divider, 0.9),
                      backgroundColor: alpha(theme.palette.background.default, 0.36),
                      color: 'text.secondary',
                      '& .MuiChip-label': {
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: 700,
                      },
                    }}
                  />
                ))}
              </Stack>
            ) : null}

            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={compact ? layoutGap.comfort : 1.25}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                useFlexGap
              >
                <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                  <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 720, lineHeight: 1.3 }}>
                    {model.message}
                  </Typography>
                  {model.detail ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.45 }}>
                      {model.detail}
                    </Typography>
                  ) : null}
                </Box>
                <Typography
                  variant={compact ? 'h6' : 'h5'}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 800,
                    lineHeight: 1,
                    color: displayedPercent === null ? 'text.primary' : accentColor,
                    textAlign: { xs: 'left', sm: 'right' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {progressLabel}
                </Typography>
              </Stack>
            </Box>

            <Box aria-label="Operation progress" sx={{ minWidth: 0 }}>
              <Box
                role="progressbar"
                aria-label="Operation progress"
                aria-valuemin={0}
                aria-valuemax={100}
                {...progressValueProps}
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: compact ? 5 : 6,
                  mb: compact ? 0.65 : 0.85,
                  overflow: 'hidden',
                  borderRadius: `${borderRadius.full}px`,
                  border: `1px solid ${alpha(accentColor, 0.18)}`,
                  backgroundColor: alpha(accentColor, 0.11),
                }}
              >
                {displayedPercent !== null ? (
                  <Box
                    data-testid="operation-progress-determinate-bar"
                    sx={{
                      width: `${displayedPercent}%`,
                      height: '100%',
                      borderRadius: 'inherit',
                      backgroundColor: accentColor,
                      transition: 'width 180ms ease-out',
                    }}
                  />
                ) : (
                  <Box
                    data-testid="operation-progress-indeterminate-bar"
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 'inherit',
                      backgroundColor: alpha(accentColor, 0.45),
                    }}
                  />
                )}
              </Box>
              <Stack
                direction="row"
                spacing={layoutGap.comfort}
                alignItems="center"
                justifyContent="space-between"
                useFlexGap
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                  {processedLabel || model.phase || getOperationProgressStatusLabel(displayStatus)}
                </Typography>
                {etaLabel ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 650 }}>
                    {etaLabel}
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            {operationFacts.length > 0 ? (
              <>
                <Divider />
                <Stack direction="row" spacing={layoutGap.comfort} flexWrap="wrap" useFlexGap>
                  {operationFacts.map((fact) => (
                    <Chip
                      key={`${fact.label}:${fact.value}`}
                      size="small"
                      variant="outlined"
                      label={`${fact.label}: ${fact.value}`}
                      sx={{
                        maxWidth: '100%',
                        height: 24,
                        borderColor: alpha(theme.palette.divider, 0.9),
                        color: 'text.secondary',
                        '& .MuiChip-label': {
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 600,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </>
            ) : null}
          </Stack>
        </Box>

        <OperationProgressActivityList items={activityItems} compact={compact} />
        {items.length > activityItems.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right' }}>
            {`Showing the last ${activityItems.length} of ${items.length} updates`}
          </Typography>
        ) : null}

        <OperationProgressStepList steps={steps} compact={compact} accentColor={accentColor} />
        {supportingMetrics.length > 0 ? (
          <Stack direction="row" spacing={layoutGap.comfort} flexWrap="wrap" useFlexGap>
            {supportingMetrics.map((metric) => {
              const color = metricColor(theme, metric);
              return (
                <Chip
                  key={`${metric.label}:${metric.value}`}
                  size="small"
                  variant="outlined"
                  label={`${metric.label}: ${metric.value}`}
                  sx={{
                    height: 24,
                    borderColor: alpha(color, 0.3),
                    backgroundColor: alpha(color, 0.045),
                    '& .MuiChip-label': {
                      fontWeight: 650,
                    },
                  }}
                />
              );
            })}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

